<template>
  <div id="app">
    <toast-messages :extend="extendToast" />
    <top-nav />
    <offline-mode @go-online="goOnline" />
    <maybe-offline-warning
      @go-to-offline-check="$router.push({ name: 'offline-check' })"
    />
    <template v-if="hasCheckedUser">
      <q-r-scanner />
      <popup-activity-form />
      <idle-logout-prompt />
    </template>
    <div class="page-body">
      <router-view v-if="hasCheckedUser" />
      <div v-else class="container">
        <loading-spinner />
      </div>
    </div>
    <page-footer />
    <form-sequence-modal />
    <ConfirmDialog />
    <service-worker-reload :use-register-s-w-function="useRegisterSW" />
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { Hub } from "aws-amplify/utils";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { useAbility } from "@casl/vue";
import { useHead } from "@unhead/vue";
import {
  LoadingSpinner,
  useAuthStore,
  PageFooter,
  ServiceWorkerReload,
  getAppUrl,
  ToastMessages,
  IdleLogoutPrompt,
  usePolling,
  OfflineMode,
  useOfflineMode,
} from "@switchsystems/coactive-vue-lib";
import { useRotaShiftCoverStore } from "./store/rotashiftcover";
import ConfirmDialog from "primevue/confirmdialog";
import TopNav from "@/components/TopNav.vue";
import PopupActivityForm from "@/components/activity/PopupActivityForm.vue";
import QRScanner from "@/components/qr/QRScanner.vue";
import FormSequenceModal from "@/components/form/FormSequenceModal.vue";
import MaybeOfflineWarning from "@/components/MaybeOfflineWarning.vue";
import extendToast from "@/lib/extendToast";

export default {
  name: "App",

  components: {
    TopNav,
    OfflineMode,
    LoadingSpinner,
    ToastMessages,
    PopupActivityForm,
    PageFooter,
    QRScanner,
    FormSequenceModal,
    ServiceWorkerReload,
    IdleLogoutPrompt,
    MaybeOfflineWarning,
    ConfirmDialog,
  },
  setup() {
    const authStore = useAuthStore();
    const route = useRoute();
    const router = useRouter();
    const offlineModeStore = useOfflineMode();

    const { can } = useAbility();
    const rotaShiftCoverStore = useRotaShiftCoverStore();
    usePolling([], {
      pollPendingApprovals: {
        fn: () => {
          // while this will be polled for every user, guard and only actually
          // make the request if relevant
          if (can("update", "rota.rotas")) {
            rotaShiftCoverStore.pollPendingApprovals();
          }
        },
        intervalMins: 1,
        immediate: true,
      },
    });

    const routeTitle = computed(() => route.meta.title ?? "");
    useHead({
      title: routeTitle,
      titleTemplate: "%s | Rota | CoActive Care",
    });

    const { hasCheckedUser } = storeToRefs(authStore);

    Hub.listen("authCustom", ({ payload }) => {
      switch (payload.event) {
        case "doLogout":
          // clear local storage before logging out
          rotaShiftCoverStore.$reset();
          window.location.href = `${getAppUrl("home")}/logout`;
          break;
        default:
      }
    });

    const goOnline = () => {
      console.log("Switching to online mode");
      offlineModeStore.workOnline();
      // check current route and redirect to home, or refresh the page if on home already
      if (router.currentRoute.value.name !== "Home") {
        router.push({ name: "Home" });
      } else {
        window.location.reload();
      }
    };

    return {
      onMounted,
      onUnmounted,
      hasCheckedUser,
      useRegisterSW,
      extendToast,
      goOnline,
    };
  },
};
</script>
